import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { ReportCard } from '@components/Pages/AnalyticsPage/ReportCard';
import { SegmentedControl } from '@components/ui/SegmentedControl/SegmentedControl';
import { DateField } from '@components/ui/DateField/DateField';
import { DateRangeField, type IDateRange } from '@components/ui/DateRangeField/DateRangeField';
import { selectSiteTz } from '@store/selectors/site.selectors';
import { APP_DEFAULT_TZ } from '@utils/dateUtils';
import { CardIcon } from '../CardIcon';
import { todayCap } from '../utils/dateRules';

export type IProjectOverviewSubmission =
  | { mode: 'single'; date: Date }
  | { mode: 'period'; range: IDateRange };

type Mode = 'single' | 'period';

interface IProjectOverviewCardProps {
  isSelected: boolean;
  onSelect: (name: 'projectOverview') => void;
  onSubmit: (payload: IProjectOverviewSubmission) => void;
}

export function ProjectOverviewCard({ isSelected, onSelect, onSubmit }: IProjectOverviewCardProps) {
  const { t } = useTranslation();
  const tz = useSelector(selectSiteTz) ?? APP_DEFAULT_TZ;
  const [mode, setMode] = useState<Mode>('single');
  const [date, setDate] = useState<Date | null>(null);
  const [range, setRange] = useState<IDateRange>({ from: null, to: null });

  const canSubmit = mode === 'single' ? date !== null : range.from !== null && range.to !== null;

  const handleSubmit = () => {
    if (mode === 'single' && date) onSubmit({ mode: 'single', date });
    else if (mode === 'period' && range.from && range.to) onSubmit({ mode: 'period', range });
  };

  return (
    <ReportCard
      name="projectOverview"
      title={t('analytics.cards.projectOverview.title')}
      description={t('analytics.cards.projectOverview.description')}
      icon={<CardIcon name="clipboard" />}
      isSelected={isSelected}
      onSelect={() => onSelect('projectOverview')}
      onSubmit={handleSubmit}
      submitLabel={t('analytics.createButton')}
      submitDisabled={!canSubmit}
    >
      <SegmentedControl
        value={mode}
        onChange={setMode}
        options={[
          { id: 'single', label: t('analytics.filters.switchToSingleDate') },
          { id: 'period', label: t('analytics.filters.switchToPeriod') },
        ]}
        ariaLabel={t('analytics.filters.dateSingle')}
      />
      {mode === 'single' ? (
        <DateField
          value={date}
          onChange={setDate}
          placeholder={t('analytics.filters.selectPlaceholder')}
          ariaLabel={t('analytics.filters.dateSingle')}
          maxDate={todayCap(tz)}
        />
      ) : (
        <DateRangeField
          value={range}
          onChange={setRange}
          placeholder={t('analytics.filters.selectPlaceholder')}
          ariaLabel={t('analytics.filters.datePeriod')}
          maxDate={todayCap(tz)}
        />
      )}
    </ReportCard>
  );
}
